import React, { useState, useEffect } from 'react';
import { Upload, Button, Input, List, Row, Col, message, Spin } from 'antd';
import { UploadOutlined, DeleteOutlined, SaveOutlined } from '@ant-design/icons';
import axios from 'axios';
import '../../styles/AttachmentButton.css';
import { useMutation, gql } from '@apollo/client'; // Corrected to useMutation

const DELETE_ATTACHMENT_MUTATION = gql`
  mutation DeleteArticleAttachment($attachmentId: Float!) {
    deleteArticleAttachment(attachmentId: $attachmentId) {
      id
    }
  }
`;

const FileUpload = ({ onAttachmentsChange, initialAttachments }) => {
  const [deleteAttachment] = useMutation(DELETE_ATTACHMENT_MUTATION);
  const [attachments, setAttachments] = useState([]);
  const [fileList, setFileList] = useState([]);
  const [savingIndex, setSavingIndex] = useState(null);

  useEffect(() => {
    console.log(initialAttachments);
    setAttachments(initialAttachments);
  }, [initialAttachments]);

  const handleFileChange = ({ file, fileList }) => {
    if (file.status === 'removed') {
      const updatedAttachments = attachments.filter((attachment) => attachment.file.uid !== file.uid);
      setAttachments(updatedAttachments);
      setFileList(fileList);
      onAttachmentsChange(updatedAttachments);
      return;
    }
    setFileList(fileList);
  };

  const handleAddAttachment = (file) => {
    const safeAttachments = Array.isArray(attachments) ? attachments : [];
    const updatedAttachments = [...safeAttachments, { file, label: '', url: null }];
    setAttachments(updatedAttachments);
    onAttachmentsChange(updatedAttachments);
  };
  
  const handleLabelChange = (e, index) => {
    const updatedAttachments = [...attachments];
    updatedAttachments[index].label = e.target.value;
    setAttachments(updatedAttachments);
    onAttachmentsChange(updatedAttachments);
  };

  const handleRemoveAttachment = async (index) => {
    const attachmentToRemove = attachments[index];
    const attachmentId = attachmentToRemove.id; // Assuming the attachment has an `id` field

    try {
      const { data } = await deleteAttachment({
        variables: { attachmentId: parseInt(attachmentId,0) },
      });

      if (data) {
        message.success('Attachment deleted successfully!');
      }
    } catch (error) {
      message.error('Failed to delete the attachment.');
      console.error(error);
    }

    const updatedAttachments = attachments.filter((_, i) => i !== index);
    const updatedFileList = fileList.filter((_, i) => i !== index);
    setAttachments(updatedAttachments);
    setFileList(updatedFileList);
    onAttachmentsChange(updatedAttachments);
  };

  const beforeUpload = (file) => {
    const isFileValid = file.size / 1024 / 1024 < 10; // Max size of 10MB
    if (!isFileValid) {
      message.error(`${file.name} is larger than 10MB`);
    }
    return isFileValid || Upload.LIST_IGNORE;
  };

  const handleSaveAttachment = async (index) => {
    setSavingIndex(index);
    try {
      const file = attachments[index].file;

      // Upload the file to Cloudinary
      const formData = new FormData();
      formData.append('file', file);
      formData.append('upload_preset', 'z6ipfpby'); // Replace with your Cloudinary upload preset

      const response = await axios.post('https://api.cloudinary.com/v1_1/dgkmgd6tv/upload', formData);

      const url = response.data.secure_url;
      const updatedAttachments = [...attachments];
      updatedAttachments[index].url = url;
      setAttachments(updatedAttachments);
      onAttachmentsChange(updatedAttachments);
      message.success(`${file.name} uploaded successfully!`);
    } catch (error) {
      message.error('Failed to upload the file.');
    } finally {
      setSavingIndex(null);
    }
  };

  return (
    <div>
      {/* Upload Button */}
      <Upload
        beforeUpload={beforeUpload}
        onChange={handleFileChange}
        multiple
        fileList={fileList}
        showUploadList={false}
        customRequest={({ file, onSuccess }) => {
          setTimeout(() => {
            handleAddAttachment(file);
            onSuccess('ok');
          }, 0);
        }}
      >
        <Button
          type="primary"
          shape="round"
          size="large"
          icon={<UploadOutlined />}
          className="custom-upload-button"
        >
          Add Attachments
        </Button>
      </Upload>

      {/* List of Attachments */}
      <List
        style={{ marginTop: '16px' }}
        bordered
        dataSource={attachments}
        renderItem={(attachment, index) => (
          <List.Item
            actions={[
              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={() => handleSaveAttachment(index)}
                disabled={!!attachment.url || savingIndex === index}
              >
                {savingIndex === index ? <Spin /> : 'Save'}
              </Button>,
              <Button
                danger
                icon={<DeleteOutlined />}
                onClick={() => handleRemoveAttachment(index)}
              >
                Remove
              </Button>,
            ]}
          >
            <Row style={{ width: '100%' }} gutter={16}>
              <Col span={8}>
                <span>{attachment.file?.name}</span>
              </Col>
              <Col span={8}>
                <Input
                  placeholder="Enter label for the file"
                  value={attachment.label}
                  onChange={(e) => handleLabelChange(e, index)}
                  disabled={!!attachment.url || savingIndex === index}
                />
              </Col>
              <Col span={8}>
                {attachment.url && <a href={attachment.url} target="_blank" rel="noopener noreferrer">View File</a>}
              </Col>
            </Row>
          </List.Item>
        )}
      />
    </div>
  );
};

export default FileUpload;
